import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { toast } from 'sonner';
import { 
  Upload, FileText, Clock, AlertCircle, CheckCircle2, 
  RefreshCw, Mail, Database, Inbox, ArrowRight, Settings, DollarSign,
  Server, Play, Square, Wifi, WifiOff, Plus, Trash2, TestTube, Loader2
} from 'lucide-react';

// UI Components
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Separator } from '../components/ui/separator';

// Import Email Ingestion Page component
import EmailIngestionPage from './EmailIngestionPage';

// API
import { uploadDocument, listDocuments } from '../lib/api';
import { formatRelativeTime } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function UploadZone({ onUploadComplete }) {
  const queryClient = useQueryClient();
  const [uploading, setUploading] = useState(false);

  const uploadMutation = useMutation({
    mutationFn: uploadDocument,
    onSuccess: (data) => {
      toast.success(`${data.filename} uploaded successfully!`);
      queryClient.invalidateQueries(['documents']);
      queryClient.invalidateQueries(['ingestionStats']);
      if (onUploadComplete) onUploadComplete();
    },
    onError: (error) => {
      toast.error(`Upload failed: ${error.message}`);
    },
  });

  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    
    setUploading(true);
    try {
      for (const file of acceptedFiles) {
        await uploadMutation.mutateAsync(file);
      }
    } finally {
      setUploading(false);
    }
  }, [uploadMutation]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    multiple: true,
  });

  return (
    <div
      {...getRootProps()}
      data-testid="ingest-upload-dropzone"
      className={`
        border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all
        ${isDragActive 
          ? 'border-primary bg-primary/5 scale-[1.02]' 
          : 'border-border hover:border-primary/50 hover:bg-muted/30'
        }
        ${uploading ? 'opacity-50 cursor-not-allowed' : ''}
      `}
    >
      <input {...getInputProps()} />
      <div className="flex flex-col items-center space-y-3">
        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
          {uploading ? (
            <RefreshCw className="w-8 h-8 text-primary animate-spin" />
          ) : (
            <Upload className="w-8 h-8 text-primary" />
          )}
        </div>
        
        {uploading ? (
          <div>
            <p className="text-sm font-medium text-foreground">Uploading...</p>
            <p className="text-xs text-muted-foreground mt-1">Please wait</p>
          </div>
        ) : isDragActive ? (
          <div>
            <p className="text-sm font-medium text-primary">Drop files here</p>
            <p className="text-xs text-muted-foreground mt-1">Release to upload</p>
          </div>
        ) : (
          <div>
            <p className="text-sm font-medium text-foreground">
              Drag & drop EOB PDFs here
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              or <Button variant="link" className="h-auto p-0 text-xs" data-testid="ingest-browse-button">browse files</Button>
            </p>
            <p className="text-xs text-muted-foreground mt-3">
              Supports: PDF • Max size: 50MB per file
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function IngestionStats() {
  const { data: workflowStatus } = useQuery({
    queryKey: ['workflowStatus'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/workflow/status`);
      if (!res.ok) throw new Error('Failed to fetch workflow status');
      return res.json();
    },
    refetchInterval: 5000
  });

  const { data: documents } = useQuery({
    queryKey: ['documents', { limit: 100 }],
    queryFn: () => listDocuments({ limit: 100 }),
    refetchInterval: 5000
  });

  const docs = documents?.documents || [];
  const todayDocs = docs.filter(d => {
    const uploadDate = new Date(d.upload_date);
    const today = new Date();
    return uploadDate.toDateString() === today.toDateString();
  });

  const stats = {
    totalIngested: workflowStatus?.documents?.total_extracted || 0,
    todayIngested: todayDocs.length,
    pendingPipeline: workflowStatus?.documents?.pending_pipeline || 0,
    completionRate: workflowStatus?.pipeline_completion_rate || 0
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <Card>
        <CardHeader className="pb-2">
          <CardDescription className="text-xs">Total Ingested</CardDescription>
          <CardTitle className="text-2xl">{stats.totalIngested}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-2 text-xs text-muted-foreground">
            <Inbox className="w-3 h-3" />
            <span>All time</span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardDescription className="text-xs">Today</CardDescription>
          <CardTitle className="text-2xl text-primary">{stats.todayIngested}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-2 text-xs text-muted-foreground">
            <Clock className="w-3 h-3" />
            <span>Documents</span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardDescription className="text-xs">Pending Pipeline</CardDescription>
          <CardTitle className="text-2xl text-amber-600">{stats.pendingPipeline}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-2 text-xs text-muted-foreground">
            <AlertCircle className="w-3 h-3" />
            <span>Awaiting processing</span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardDescription className="text-xs">Pipeline Complete</CardDescription>
          <CardTitle className="text-2xl text-emerald-600">{stats.completionRate}%</CardTitle>
        </CardHeader>
        <CardContent>
          <Progress value={stats.completionRate} className="h-1.5" />
        </CardContent>
      </Card>
    </div>
  );
}

function RecentIngestions() {
  const navigate = useNavigate();
  
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['recentIngestions'],
    queryFn: () => listDocuments({ limit: 10 }),
    refetchInterval: 5000
  });

  const documents = data?.documents || [];

  const getStatusIcon = (status) => {
    switch (status) {
      case 'complete':
        return <CheckCircle2 className="w-4 h-4 text-emerald-500" />;
      case 'processing':
        return <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />;
      case 'failed':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-amber-500" />;
    }
  };

  const getSourceBadge = (source) => {
    switch (source) {
      case 'email':
        return <Badge variant="outline" className="text-xs"><Mail className="w-3 h-3 mr-1" />Email</Badge>;
      case 'edi':
        return <Badge variant="outline" className="text-xs"><Database className="w-3 h-3 mr-1" />EDI</Badge>;
      default:
        return <Badge variant="outline" className="text-xs"><Upload className="w-3 h-3 mr-1" />Upload</Badge>;
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg">Recent Ingestions</CardTitle>
            <CardDescription>Latest documents received</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead>Document</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Payer</TableHead>
                <TableHead>Ingested</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ) : documents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                    No documents ingested yet
                  </TableCell>
                </TableRow>
              ) : (
                documents.map((doc) => (
                  <TableRow
                    key={doc.id}
                    data-testid="ingestion-row"
                    className="cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => navigate(`/documents/${doc.id}`)}
                  >
                    <TableCell className="font-medium">
                      <div className="flex items-center space-x-2">
                        <FileText className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm truncate max-w-[200px]">{doc.filename}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {getSourceBadge(doc.source_channel)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(doc.processing_status)}
                        <span className="text-sm capitalize">{doc.processing_status}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">
                      {doc.payer_name || <span className="text-muted-foreground">-</span>}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {formatRelativeTime(doc.upload_date)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

function ManualUploadTab() {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Upload className="w-5 h-5" />
            <span>Manual Document Upload</span>
          </CardTitle>
          <CardDescription>
            Upload EOB documents directly from your computer
          </CardDescription>
        </CardHeader>
        <CardContent>
          <UploadZone />
        </CardContent>
      </Card>
      
      <RecentIngestions />
    </div>
  );
}

function EDITab() {
  const queryClient = useQueryClient();
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [showSftpDialog, setShowSftpDialog] = useState(false);
  const [testingConfig, setTestingConfig] = useState(null);
  const [pollingConfig, setPollingConfig] = useState(null);

  // SFTP Form State
  const [sftpForm, setSftpForm] = useState({
    name: '',
    clearinghouse_name: '',
    host: '',
    port: 22,
    username: '',
    auth_type: 'password',
    password: '',
    remote_directory: '/',
    file_pattern: '*.835',
    polling_enabled: false,
    polling_interval: 60,
    auto_process: true
  });

  const { data: ediStats, isLoading: loadingStats } = useQuery({
    queryKey: ['ediStats'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/ingestion/edi/stats`);
      if (!res.ok) throw new Error('Failed to fetch EDI stats');
      return res.json();
    },
    refetchInterval: 10000
  });

  const { data: ediFiles, isLoading: loadingFiles, refetch: refetchFiles } = useQuery({
    queryKey: ['ediFiles'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/ingestion/edi/files?limit=10`);
      if (!res.ok) throw new Error('Failed to fetch EDI files');
      return res.json();
    }
  });

  const { data: sftpConfigs, isLoading: loadingSftp, refetch: refetchSftp } = useQuery({
    queryKey: ['sftpConfigs'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/ingestion/sftp/configs`);
      if (!res.ok) throw new Error('Failed to fetch SFTP configs');
      return res.json();
    }
  });

  const { data: sftpStats } = useQuery({
    queryKey: ['sftpStats'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/ingestion/sftp/stats`);
      if (!res.ok) throw new Error('Failed to fetch SFTP stats');
      return res.json();
    },
    refetchInterval: 10000
  });

  const handleFileDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = e.dataTransfer?.files || e.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    await uploadEDIFile(file);
  };

  const uploadEDIFile = async (file) => {
    setUploading(true);
    setUploadResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(`${API_URL}/api/ingestion/edi/upload`, {
        method: 'POST',
        body: formData
      });

      const result = await res.json();
      
      if (res.ok && result.success) {
        toast.success(`EDI file processed: ${result.claims_processed} claims`);
        setUploadResult(result);
        queryClient.invalidateQueries(['ediStats']);
        queryClient.invalidateQueries(['ediFiles']);
        queryClient.invalidateQueries(['documents']);
      } else {
        toast.error(result.message || 'Failed to process EDI file');
        setUploadResult({ success: false, errors: result.errors || [result.message] });
      }
    } catch (error) {
      toast.error(`Upload failed: ${error.message}`);
      setUploadResult({ success: false, errors: [error.message] });
    } finally {
      setUploading(false);
    }
  };

  const handleCreateSftpConfig = async () => {
    try {
      const payload = {
        name: sftpForm.name,
        clearinghouse_name: sftpForm.clearinghouse_name || null,
        host: sftpForm.host,
        port: sftpForm.port,
        username: sftpForm.username,
        auth_type: sftpForm.auth_type,
        password: sftpForm.password || null,
        remote_directory: sftpForm.remote_directory,
        file_pattern: { pattern: sftpForm.file_pattern, include_subdirs: false, max_file_age_days: 30 },
        polling: {
          enabled: sftpForm.polling_enabled,
          interval_minutes: sftpForm.polling_interval,
          delete_after_download: false,
          move_after_download: false
        },
        auto_process: sftpForm.auto_process,
        is_active: true
      };

      const res = await fetch(`${API_URL}/api/ingestion/sftp/configs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        toast.success('SFTP configuration created');
        setShowSftpDialog(false);
        setSftpForm({
          name: '', clearinghouse_name: '', host: '', port: 22, username: '',
          auth_type: 'password', password: '', remote_directory: '/',
          file_pattern: '*.835', polling_enabled: false, polling_interval: 60, auto_process: true
        });
        refetchSftp();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create configuration');
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    }
  };

  const handleTestConnection = async (configId) => {
    setTestingConfig(configId);
    try {
      const res = await fetch(`${API_URL}/api/ingestion/sftp/configs/${configId}/test`, {
        method: 'POST'
      });
      const result = await res.json();
      
      if (result.success) {
        toast.success(`Connected! Found ${result.remote_files_found} matching files`);
      } else {
        toast.error(result.message || 'Connection failed');
      }
      refetchSftp();
    } catch (error) {
      toast.error(`Test failed: ${error.message}`);
    } finally {
      setTestingConfig(null);
    }
  };

  const handlePollNow = async (configId) => {
    setPollingConfig(configId);
    try {
      const res = await fetch(`${API_URL}/api/ingestion/sftp/configs/${configId}/poll-now`, {
        method: 'POST'
      });
      const result = await res.json();
      
      if (result.success) {
        toast.success(`Poll complete: ${result.files_downloaded} downloaded, ${result.files_processed} processed`);
        queryClient.invalidateQueries(['ediStats']);
        queryClient.invalidateQueries(['ediFiles']);
        queryClient.invalidateQueries(['sftpStats']);
      } else {
        toast.error(result.errors?.join(', ') || 'Poll failed');
      }
      refetchSftp();
    } catch (error) {
      toast.error(`Poll failed: ${error.message}`);
    } finally {
      setPollingConfig(null);
    }
  };

  const handleDeleteConfig = async (configId) => {
    if (!window.confirm('Are you sure you want to delete this configuration?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/ingestion/sftp/configs/${configId}`, {
        method: 'DELETE'
      });
      
      if (res.ok) {
        toast.success('Configuration deleted');
        refetchSftp();
      } else {
        toast.error('Failed to delete configuration');
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value || 0);
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'active': { variant: 'default', className: 'bg-emerald-100 text-emerald-700', icon: Wifi },
      'inactive': { variant: 'secondary', className: '', icon: WifiOff },
      'error': { variant: 'destructive', className: '', icon: AlertCircle },
      'testing': { variant: 'outline', className: '', icon: Loader2 },
      'polling': { variant: 'default', className: 'bg-blue-100 text-blue-700', icon: RefreshCw }
    };
    const config = statusMap[status] || statusMap['inactive'];
    const Icon = config.icon;
    return (
      <Badge variant={config.variant} className={config.className}>
        <Icon className={`w-3 h-3 mr-1 ${status === 'testing' || status === 'polling' ? 'animate-spin' : ''}`} />
        {status}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      {/* EDI Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="text-xs">Total EDI Files</CardDescription>
            <CardTitle className="text-2xl">{loadingStats ? '...' : ediStats?.total_files || 0}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center space-x-2 text-xs text-muted-foreground">
              <Database className="w-3 h-3" />
              <span>Processed</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="text-xs">Total Claims</CardDescription>
            <CardTitle className="text-2xl text-primary">{loadingStats ? '...' : ediStats?.total_claims || 0}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center space-x-2 text-xs text-muted-foreground">
              <FileText className="w-3 h-3" />
              <span>From EDI files</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="text-xs">SFTP Connections</CardDescription>
            <CardTitle className="text-2xl">{sftpStats?.configs?.active || 0}<span className="text-sm text-muted-foreground">/{sftpStats?.configs?.total || 0}</span></CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center space-x-2 text-xs text-muted-foreground">
              <Server className="w-3 h-3" />
              <span>Active connections</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="text-xs">Total Paid</CardDescription>
            <CardTitle className="text-xl text-emerald-600">{loadingStats ? '...' : formatCurrency(ediStats?.total_paid)}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center space-x-2 text-xs text-muted-foreground">
              <CheckCircle2 className="w-3 h-3" />
              <span>Payments</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* SFTP Clearinghouse Connections */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center space-x-2">
                <Server className="w-5 h-5" />
                <span>Clearinghouse SFTP Connections</span>
              </CardTitle>
              <CardDescription>
                Configure automated EDI file retrieval from clearinghouses via SFTP
              </CardDescription>
            </div>
            <Dialog open={showSftpDialog} onOpenChange={setShowSftpDialog}>
              <DialogTrigger asChild>
                <Button data-testid="add-sftp-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Connection
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                  <DialogTitle>Add SFTP Connection</DialogTitle>
                  <DialogDescription>
                    Configure a new clearinghouse SFTP connection for automated EDI file retrieval
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Connection Name *</Label>
                      <Input
                        id="name"
                        placeholder="e.g., Availity Production"
                        value={sftpForm.name}
                        onChange={(e) => setSftpForm({...sftpForm, name: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="clearinghouse">Clearinghouse</Label>
                      <Input
                        id="clearinghouse"
                        placeholder="e.g., Availity, Change Healthcare"
                        value={sftpForm.clearinghouse_name}
                        onChange={(e) => setSftpForm({...sftpForm, clearinghouse_name: e.target.value})}
                      />
                    </div>
                  </div>

                  <Separator />

                  <div className="grid grid-cols-3 gap-4">
                    <div className="col-span-2 space-y-2">
                      <Label htmlFor="host">SFTP Host *</Label>
                      <Input
                        id="host"
                        placeholder="sftp.clearinghouse.com"
                        value={sftpForm.host}
                        onChange={(e) => setSftpForm({...sftpForm, host: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="port">Port</Label>
                      <Input
                        id="port"
                        type="number"
                        value={sftpForm.port}
                        onChange={(e) => setSftpForm({...sftpForm, port: parseInt(e.target.value) || 22})}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="username">Username *</Label>
                      <Input
                        id="username"
                        placeholder="sftp_user"
                        value={sftpForm.username}
                        onChange={(e) => setSftpForm({...sftpForm, username: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="password">Password *</Label>
                      <Input
                        id="password"
                        type="password"
                        placeholder="••••••••"
                        value={sftpForm.password}
                        onChange={(e) => setSftpForm({...sftpForm, password: e.target.value})}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="remote_dir">Remote Directory</Label>
                      <Input
                        id="remote_dir"
                        placeholder="/outbound/835"
                        value={sftpForm.remote_directory}
                        onChange={(e) => setSftpForm({...sftpForm, remote_directory: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="file_pattern">File Pattern</Label>
                      <Input
                        id="file_pattern"
                        placeholder="*.835"
                        value={sftpForm.file_pattern}
                        onChange={(e) => setSftpForm({...sftpForm, file_pattern: e.target.value})}
                      />
                    </div>
                  </div>

                  <Separator />

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Automated Polling</Label>
                      <p className="text-xs text-muted-foreground">Automatically fetch new files</p>
                    </div>
                    <Switch
                      checked={sftpForm.polling_enabled}
                      onCheckedChange={(checked) => setSftpForm({...sftpForm, polling_enabled: checked})}
                    />
                  </div>

                  {sftpForm.polling_enabled && (
                    <div className="space-y-2">
                      <Label htmlFor="interval">Polling Interval (minutes)</Label>
                      <Select
                        value={String(sftpForm.polling_interval)}
                        onValueChange={(val) => setSftpForm({...sftpForm, polling_interval: parseInt(val)})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="15">Every 15 minutes</SelectItem>
                          <SelectItem value="30">Every 30 minutes</SelectItem>
                          <SelectItem value="60">Every hour</SelectItem>
                          <SelectItem value="120">Every 2 hours</SelectItem>
                          <SelectItem value="360">Every 6 hours</SelectItem>
                          <SelectItem value="720">Every 12 hours</SelectItem>
                          <SelectItem value="1440">Daily</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Auto-Process Files</Label>
                      <p className="text-xs text-muted-foreground">Parse and import EDI files automatically</p>
                    </div>
                    <Switch
                      checked={sftpForm.auto_process}
                      onCheckedChange={(checked) => setSftpForm({...sftpForm, auto_process: checked})}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowSftpDialog(false)}>Cancel</Button>
                  <Button 
                    onClick={handleCreateSftpConfig}
                    disabled={!sftpForm.name || !sftpForm.host || !sftpForm.username}
                  >
                    Create Connection
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {loadingSftp ? (
            <div className="text-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto text-muted-foreground" />
            </div>
          ) : sftpConfigs?.configs?.length > 0 ? (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>Connection</TableHead>
                    <TableHead>Host</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Files</TableHead>
                    <TableHead>Last Poll</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sftpConfigs.configs.map((config) => (
                    <TableRow key={config.id} className="hover:bg-muted/30">
                      <TableCell>
                        <div>
                          <div className="font-medium">{config.name}</div>
                          {config.clearinghouse_name && (
                            <div className="text-xs text-muted-foreground">{config.clearinghouse_name}</div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        {config.host}:{config.port}
                      </TableCell>
                      <TableCell>{getStatusBadge(config.status)}</TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {config.files_downloaded || 0} <span className="text-muted-foreground">downloaded</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {config.last_poll ? formatRelativeTime(config.last_poll) : 'Never'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleTestConnection(config.id)}
                            disabled={testingConfig === config.id}
                            title="Test Connection"
                          >
                            {testingConfig === config.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <TestTube className="w-4 h-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handlePollNow(config.id)}
                            disabled={pollingConfig === config.id}
                            title="Poll Now"
                          >
                            {pollingConfig === config.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Play className="w-4 h-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteConfig(config.id)}
                            className="text-destructive hover:text-destructive"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8 border-2 border-dashed rounded-xl">
              <Server className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">No SFTP connections configured</p>
              <p className="text-xs text-muted-foreground mt-1">Add a connection to enable automated EDI file retrieval</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Manual Upload Zone */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Database className="w-5 h-5" />
            <span>Manual EDI 835 Upload</span>
          </CardTitle>
          <CardDescription>
            Upload EDI 835 remittance files manually for processing
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div
            data-testid="edi-upload-dropzone"
            className={`
              border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all
              ${isDragging 
                ? 'border-primary bg-primary/5 scale-[1.02]' 
                : 'border-border hover:border-primary/50 hover:bg-muted/30'
              }
              ${uploading ? 'opacity-50 cursor-not-allowed' : ''}
            `}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleFileDrop}
            onClick={() => !uploading && document.getElementById('edi-file-input').click()}
          >
            <input
              type="file"
              id="edi-file-input"
              className="hidden"
              accept=".txt,.edi,.835,.x12"
              onChange={handleFileDrop}
            />
            <div className="flex flex-col items-center space-y-3">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                {uploading ? (
                  <RefreshCw className="w-8 h-8 text-primary animate-spin" />
                ) : (
                  <Database className="w-8 h-8 text-primary" />
                )}
              </div>
              
              {uploading ? (
                <div>
                  <p className="text-sm font-medium text-foreground">Processing EDI file...</p>
                  <p className="text-xs text-muted-foreground mt-1">Parsing claims and payments</p>
                </div>
              ) : isDragging ? (
                <div>
                  <p className="text-sm font-medium text-primary">Drop EDI file here</p>
                  <p className="text-xs text-muted-foreground mt-1">Release to upload</p>
                </div>
              ) : (
                <div>
                  <p className="text-sm font-medium text-foreground">
                    Drag & drop EDI 835 files here
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    or click to browse files
                  </p>
                  <p className="text-xs text-muted-foreground mt-3">
                    Supports: .txt, .edi, .835, .x12 files
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Upload Result */}
          {uploadResult && (
            <div className={`mt-4 p-4 rounded-lg ${uploadResult.success ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
              {uploadResult.success ? (
                <div className="space-y-2">
                  <div className="flex items-center space-x-2 text-emerald-700">
                    <CheckCircle2 className="w-5 h-5" />
                    <span className="font-medium">EDI file processed successfully!</span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm text-emerald-600">
                    <div>Claims: {uploadResult.claims_processed}</div>
                    <div>Payer: {uploadResult.payer?.name || 'N/A'}</div>
                    <div>Check #: {uploadResult.payment_info?.check_number || 'N/A'}</div>
                    <div>Total Paid: {formatCurrency(uploadResult.summary?.total_paid)}</div>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center space-x-2 text-red-700">
                    <AlertCircle className="w-5 h-5" />
                    <span className="font-medium">Failed to process EDI file</span>
                  </div>
                  {uploadResult.errors && (
                    <ul className="text-sm text-red-600 list-disc list-inside">
                      {uploadResult.errors.map((err, i) => <li key={i}>{err}</li>)}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent EDI Files */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Recent EDI Files</CardTitle>
              <CardDescription>Recently processed EDI 835 files from all sources</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={() => refetchFiles()}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loadingFiles ? (
            <div className="text-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto text-muted-foreground" />
            </div>
          ) : ediFiles?.files?.length > 0 ? (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>Filename</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Payer</TableHead>
                    <TableHead>Claims</TableHead>
                    <TableHead>Total Paid</TableHead>
                    <TableHead>Uploaded</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ediFiles.files.map((file) => (
                    <TableRow key={file.id} className="hover:bg-muted/30">
                      <TableCell className="font-medium">
                        <div className="flex items-center space-x-2">
                          <Database className="w-4 h-4 text-muted-foreground" />
                          <span className="truncate max-w-[200px]">{file.filename}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {file.source_channel === 'sftp' ? 'SFTP' : file.source_channel?.toUpperCase() || 'Manual'}
                        </Badge>
                      </TableCell>
                      <TableCell>{file.payer_info?.name || 'N/A'}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">{file.claim_count || 0}</Badge>
                      </TableCell>
                      <TableCell>{formatCurrency(file.summary?.total_paid)}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {formatRelativeTime(file.upload_date)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8 border-2 border-dashed rounded-xl">
              <Database className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">No EDI files uploaded yet</p>
              <p className="text-xs text-muted-foreground mt-1">Upload files manually or configure SFTP connections above</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function IngestPage() {
  const [activeTab, setActiveTab] = useState('upload');
  const navigate = useNavigate();

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6" data-testid="ingest-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground font-heading">Ingest</h1>
          <p className="text-muted-foreground mt-1">Receive documents from multiple channels</p>
        </div>
        <div className="flex items-center gap-3 mt-4 sm:mt-0">
          <Button variant="outline" onClick={() => navigate('/workflow')}>
            <ArrowRight className="w-4 h-4 mr-2" />
            View Pipeline
          </Button>
        </div>
      </div>

      {/* Stats */}
      <IngestionStats />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 lg:w-auto lg:inline-grid">
          <TabsTrigger value="upload" className="flex items-center" data-testid="tab-manual-upload">
            <Upload className="w-4 h-4 mr-2" />
            Manual Upload
          </TabsTrigger>
          <TabsTrigger value="email" className="flex items-center" data-testid="tab-email">
            <Mail className="w-4 h-4 mr-2" />
            Email Ingestion
          </TabsTrigger>
          <TabsTrigger value="edi" className="flex items-center" data-testid="tab-edi">
            <Database className="w-4 h-4 mr-2" />
            EDI 835
          </TabsTrigger>
        </TabsList>

        <TabsContent value="upload" className="mt-6">
          <ManualUploadTab />
        </TabsContent>

        <TabsContent value="email" className="mt-6">
          <EmailIngestionPage />
        </TabsContent>

        <TabsContent value="edi" className="mt-6">
          <EDITab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
